import debounce from 'react-utils/debounce';
import emptyFunction from 'react-utils/emptyFunction';
import { useEffect, useMemo, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import styled from 'foundations-theming/utils';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Small } from 'foundations-components/transitional/components/Small';
import { Button } from 'foundations-components/transitional/components/Button';
import { ThumbsDownIcon } from 'foundations-assets/icons/components/ThumbsDownIcon';
import { ThumbsUpIcon } from 'foundations-assets/icons/components/ThumbsUpIcon';
import { H5 } from 'foundations-components/transitional/components/H5';
import DoubleThumbsUpSvgIcon from './DoubleThumbsUpSvgIcon';
import RatingsPopoverButton from './RatingsPopoverButton';
import UsageTracker from '../../../utils/UsageTracker';
import { useGetPreviousReview, useGetReviewTagsHelper } from '../hooks/useRatingsHooks';
import { useGetReviewStats } from '../queries/ratingsQueries';
import { HasScope, useHasScope, useUserInfo } from '../../accounts';
import { AcademyRatingsTypes, RatingsReviewGroup } from '../../../constants/RatingsAndReviewConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RatingsIconWrapper = styled.div.withConfig({
  displayName: "DesktopRatingsContainer__RatingsIconWrapper",
  componentId: "sc-1jfgziz-0"
})(["display:flex;align-items:center;justify-content:center;width:44px;height:48px;background-color:", ";border-radius:", ";color:", ";svg{fill:", ";}"], ({
  $isActive,
  theme
}) => $isActive ? theme.color['fill-accent-neutral-subtle'] : 'transparent', ({
  theme
}) => theme.borderRadius['200'], ({
  $isActive,
  theme
}) => $isActive ? theme.color['text-inverse-default'] : theme.color['icon-core-default'], ({
  $isActive,
  theme
}) => $isActive ? theme.color['text-inverse-default'] : theme.color['icon-core-default']);
const VerticalDivider = () => /*#__PURE__*/_jsx(View, {
  as: "span",
  borderColor: "border-core-default",
  borderInlineEndStyle: "solid",
  borderInlineWidth: "100",
  marginInline: "100",
  position: "relative"
});
const DesktopRatingsContainer = ({
  additionalTrackingData = {},
  contentId,
  reviewType
}) => {
  const [rating, setRating] = useState(0);
  const [displayLovePopover, setDisplayLovePopover] = useState(false);
  const [displayLikePopover, setDisplayLikePopover] = useState(false);
  const [displayDislikePopover, setDisplayDislikePopover] = useState(false);
  const {
    userInfo
  } = useUserInfo();
  const {
    hasScope: hasRatingsAdvanced
  } = useHasScope('academy-ratings-advanced');
  const trackingContentType = reviewType === AcademyRatingsTypes.ACADEMY_COURSE ? 'TRACK' : reviewType === AcademyRatingsTypes.ACADEMY_LESSON ? 'LESSON' : 'SHORT_FORM_CONTENT';
  const ratingsReviewGroupKey = rating === 2 || rating === 3 ? RatingsReviewGroup.LIKE_FEEDBACK : RatingsReviewGroup.DISLIKE_FEEDBACK;
  const {
    previousReview,
    refetchSearchReview
  } = useGetPreviousReview({
    entityId: contentId,
    reviewTypes: [reviewType],
    reviewerUserId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.id
  });
  const previousRating = previousReview === null || previousReview === void 0 ? void 0 : previousReview.ratings.OVERALL;
  const isRatingEditing = !!previousRating;
  useEffect(() => {
    UsageTracker.track('interaction', {
      action: 'ratings-and-review-viewed',
      id: contentId,
      type: trackingContentType
    });
  }, [contentId, trackingContentType]);
  useEffect(() => {
    if (previousRating) {
      setRating(previousRating);
    }
  }, [previousRating]);
  const {
    getReviewTags,
    ratingsTagKeysMap
  } = useGetReviewTagsHelper();
  const reviewTags = useMemo(() => getReviewTags(ratingsReviewGroupKey), [ratingsReviewGroupKey, getReviewTags]);
  const {
    ratingsStats,
    refetchRatings
  } = useGetReviewStats({
    variables: {
      entityId: contentId,
      reviewType
    }
  });
  const ratingsDistributions = ratingsStats ? ratingsStats.axes.OVERALL.distribution : {
    1: 0,
    2: 0,
    3: 0
  };
  const handleOnClose = () => {
    setDisplayLovePopover(false);
    setDisplayLikePopover(false);
    setDisplayDislikePopover(false);
    setRating(!previousRating ? 0 : previousRating);
  };
  const handleButtonClick = newRating => () => {
    UsageTracker.track('interaction', {
      action: 'ratings-popover-viewed',
      id: contentId,
      type: trackingContentType
    });
    UsageTracker.track('ratingInteraction', Object.assign({
      action: isRatingEditing ? 'RATING-THUMB-EDIT' : 'RATING-THUMB-CREATE',
      contentId: Number(contentId),
      contentType: trackingContentType,
      ratingScore: newRating,
      view: 'desktop'
    }, additionalTrackingData));
    setRating(newRating);
    switch (newRating) {
      case 3:
        setDisplayLovePopover(true);
        setDisplayLikePopover(false);
        setDisplayDislikePopover(false);
        break;
      case 2:
        setDisplayLovePopover(false);
        setDisplayLikePopover(true);
        setDisplayDislikePopover(false);
        break;
      default:
        setDisplayLovePopover(false);
        setDisplayLikePopover(false);
        setDisplayDislikePopover(true);
    }
  };
  const delayedRefetchReviewsData = debounce(() => {
    refetchSearchReview().catch(emptyFunction);
    refetchRatings().catch(emptyFunction);
  }, 1000);
  const handleOnSuccess = review => {
    var _review$tags$ratingsR;
    const ratingReason = ratingsTagKeysMap && (review === null || review === void 0 || (_review$tags$ratingsR = review.tags[ratingsReviewGroupKey]) === null || _review$tags$ratingsR === void 0 ? void 0 : _review$tags$ratingsR.map(tagId => ratingsTagKeysMap[ratingsReviewGroupKey][tagId]));
    UsageTracker.track('ratingInteraction', Object.assign({
      action: isRatingEditing ? 'EDIT' : 'CREATE',
      contentId: Number(contentId),
      contentType: trackingContentType,
      ratingReason,
      ratingScore: rating,
      view: 'desktop'
    }, additionalTrackingData));
    delayedRefetchReviewsData();
    setDisplayLovePopover(false);
    setDisplayLikePopover(false);
    setDisplayDislikePopover(false);
  };
  return /*#__PURE__*/_jsx(View, {
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-evenly",
      minInlineSize: hasRatingsAdvanced ? '300px' : '170px',
      children: [/*#__PURE__*/_jsx(View, {
        children: /*#__PURE__*/_jsx("b", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.ratingsAndReview.feedback"
          })
        })
      }), /*#__PURE__*/_jsxs(View, {
        as: "span",
        marginInlineStart: "200",
        children: [/*#__PURE__*/_jsx(RatingsPopoverButton, {
          contentId: contentId,
          displayPopover: displayLovePopover,
          handleOnClose: handleOnClose,
          handleOnSuccess: handleOnSuccess,
          header: /*#__PURE__*/_jsx(H5, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.ratingsAndReview.popover.header.love"
            })
          }),
          previousReview: previousReview,
          rating: 3,
          ratingsReviewGroupKey: ratingsReviewGroupKey,
          reviewTags: reviewTags,
          reviewType: reviewType,
          children: /*#__PURE__*/_jsx(Button, {
            "data-selected": rating === 3,
            marginInlineStart: "0",
            onClick: handleButtonClick(3),
            use: "unstyled",
            children: /*#__PURE__*/_jsxs(RatingsIconWrapper, {
              $isActive: rating === 3,
              "data-test-id": "double-thumbs-up",
              children: [/*#__PURE__*/_jsx(DoubleThumbsUpSvgIcon, {}), /*#__PURE__*/_jsx(HasScope, {
                scope: "academy-ratings-advanced",
                children: !!ratingsDistributions[3] && /*#__PURE__*/_jsx(Small, {
                  "data-test-id": "ratings-love-statistics",
                  marginInlineStart: "100",
                  children: /*#__PURE__*/_jsx(FormattedNumber, {
                    abbreviate: true,
                    value: ratingsDistributions[3]
                  })
                })
              })]
            })
          })
        }), /*#__PURE__*/_jsx(VerticalDivider, {}), /*#__PURE__*/_jsx(RatingsPopoverButton, {
          contentId: contentId,
          displayPopover: displayLikePopover,
          handleOnClose: handleOnClose,
          handleOnSuccess: handleOnSuccess,
          header: /*#__PURE__*/_jsx(H5, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.ratingsAndReview.popover.header.like"
            })
          }),
          previousReview: previousReview,
          rating: 2,
          ratingsReviewGroupKey: ratingsReviewGroupKey,
          reviewTags: reviewTags,
          reviewType: reviewType,
          children: /*#__PURE__*/_jsx(Button, {
            "data-selected": rating === 2,
            marginInlineStart: "0",
            onClick: handleButtonClick(2),
            use: "unstyled",
            children: /*#__PURE__*/_jsxs(RatingsIconWrapper, {
              $isActive: rating === 2,
              children: [/*#__PURE__*/_jsx(ThumbsUpIcon, {
                size: "400"
              }), /*#__PURE__*/_jsx(HasScope, {
                scope: "academy-ratings-advanced",
                children: !!ratingsDistributions[2] && /*#__PURE__*/_jsx(Small, {
                  "data-test-id": "ratings-like-statistics",
                  marginInlineStart: "100",
                  children: /*#__PURE__*/_jsx(FormattedNumber, {
                    abbreviate: true,
                    value: ratingsDistributions[2]
                  })
                })
              })]
            })
          })
        }), /*#__PURE__*/_jsx(VerticalDivider, {}), /*#__PURE__*/_jsx(RatingsPopoverButton, {
          contentId: contentId,
          displayPopover: displayDislikePopover,
          handleOnClose: handleOnClose,
          handleOnSuccess: handleOnSuccess,
          header: /*#__PURE__*/_jsx(H5, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.ratingsAndReview.popover.header.dislike"
            })
          }),
          previousReview: previousReview,
          rating: 1,
          ratingsReviewGroupKey: ratingsReviewGroupKey,
          reviewTags: reviewTags,
          reviewType: reviewType,
          children: /*#__PURE__*/_jsx(Button, {
            "data-selected": rating === 1,
            onClick: handleButtonClick(1),
            use: "unstyled",
            children: /*#__PURE__*/_jsxs(RatingsIconWrapper, {
              $isActive: rating === 1,
              children: [/*#__PURE__*/_jsx(ThumbsDownIcon, {
                size: "400"
              }), /*#__PURE__*/_jsx(HasScope, {
                scope: "academy-ratings-advanced",
                children: !!ratingsDistributions[1] && /*#__PURE__*/_jsx(Small, {
                  "data-test-id": "ratings-dislike-statistics",
                  marginInlineStart: "100",
                  children: /*#__PURE__*/_jsx(FormattedNumber, {
                    abbreviate: true,
                    value: ratingsDistributions[1]
                  })
                })
              })]
            })
          })
        })]
      })]
    })
  });
};
export default DesktopRatingsContainer;